import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import GameScreenLayout from "@/components/GameScreenLayout"
import { getShopItems, purchaseShopItem } from "@/api/gameApi"
import { getGameApiErrorMessage } from "@/api/gameApiErrors"
import { formatShopReward } from "@/constants/gameMeta"
import { ELEMENT_NAMES } from "@/constants/guardians"
import { useGuardiansPassport } from "@/features/passport/GuardiansPassportContext"
import { CardElement } from "@/types/battle"

const ELEMENT_ORDER: CardElement[] = [
  "FIRE",
  "WATER",
  "EARTH",
  "ICE",
  "WIND",
  "LIGHTNING",
]

export default function DustShopPage() {
  const queryClient = useQueryClient()
  const { passport, syncGameState } = useGuardiansPassport()

  const q = useQuery({
    queryKey: ["shop-items-dust"],
    queryFn: getShopItems,
  })

  const purchase = useMutation({
    mutationFn: (itemId: string) => purchaseShopItem(itemId),
    onSuccess: async () => {
      await syncGameState()
      void queryClient.invalidateQueries({ queryKey: ["shop-items-dust"] })
    },
  })

  const items = (q.data?.items ?? []).filter(
    (item) => item.rewardType === "SHARD_DUST"
  )

  const grouped = ELEMENT_ORDER.map((element) => ({
    element,
    items: items.filter((i) => (i.rewardElement ?? "FIRE") === element),
  })).filter((g) => g.items.length > 0)

  return (
    <GameScreenLayout title="Магазин пыли" backTo="/play/shards">
      <p className="mb-4 text-sm text-guardians-muted">
        Монеты: {passport?.coins ?? 0}
      </p>
      {q.isLoading ? (
        <p className="text-sm text-guardians-muted">Загрузка…</p>
      ) : grouped.length === 0 ? (
        <p className="text-sm text-guardians-muted">Нет предложений пыли</p>
      ) : (
        grouped.map(({ element, items: list }) => (
          <section key={element} className="mb-6">
            <h2 className="mb-2 text-sm font-medium">{ELEMENT_NAMES[element]}</h2>
            <ul className="space-y-2">
              {list.map((item) => (
                <li
                  key={item.id}
                  className="flex items-center justify-between gap-2 rounded-xl border border-white/10 p-3 text-sm"
                >
                  <div>
                    <p className="font-medium">{item.title}</p>
                    <p className="text-xs text-guardians-accent">
                      {formatShopReward(item.rewardType, item.rewardAmount)}
                    </p>
                  </div>
                  <button
                    type="button"
                    disabled={purchase.isPending}
                    onClick={() => purchase.mutate(item.id)}
                    className="shrink-0 rounded-lg bg-guardians-accent px-3 py-1.5 text-xs text-guardians-bg"
                  >
                    {item.priceCoins} 🪙
                  </button>
                </li>
              ))}
            </ul>
          </section>
        ))
      )}
      {purchase.isError ? (
        <p className="text-sm text-red-300">
          {getGameApiErrorMessage(purchase.error, "Ошибка покупки")}
        </p>
      ) : null}
    </GameScreenLayout>
  )
}
